<template>
    <div class="register-form">
        <div class="title-row">
            <h1 class="title">Register</h1>
            <Button type="button" label="Back to Login" size="small" severity="secondary" text @click="showRegister = false"></Button>
        </div>
        <p class="text-surface-500 dark:text-surface-400 block subtitle">Please register with the form below</p>

        <div class="status-stack">
            <p class="status-banner error" v-if="showInvalidRegisterMessage">{{ registrationErrorMessage }}</p>
            <p class="status-banner error" v-if="showMissingFieldsMessage">Please fill in all fields</p>
            <p class="status-banner error" v-if="showPasswordMismatchMessage">Passwords do not match</p>
            <p class="status-banner error" v-if="showRegisterError">Error registering</p>
            <p class="status-banner success" v-if="showRegistrationSuccess">{{ registrationSuccessMessage }}</p>
            <p class="status-banner error" v-if="showGDPRMessage">Please check the box above to provide consent to our <a href="/gdpr">data policy</a>, which will allow the app to store your registration.</p>
        </div>

        <Divider />
        <div class="form-section">
            <h2>Username:</h2>
            <InputText v-model="username" placeholder="Username" class="wide"/>
            <h2>Email:</h2>
            <InputText v-model="email" placeholder="Email" class="wide" />
            <h2>Password:</h2>
            <InputText v-model="password" placeholder="Password" type="password" class="wide"/>
            <h2>Re-enter Password:</h2>
            <InputText v-model="passwordCheck" placeholder="Password" type="password" class="wide"/>
            <Divider />
            <h2>Data Usage Consent</h2>
            <div class="consent-copy">
                <p>For terms and conditions see our <a href="/gdpr">data policy</a>.</p>
                <p>Please confirm consent to store registration data and session cookies.</p>
            </div>
            <div class="consent-row">
                <Checkbox v-model="gdprAck" binary inputId="gdprAck" />
                <label for="gdprAck">I consent to the data policy</label>
            </div>
            <div class="consent-row">
                <Checkbox v-model="qaCookies" binary inputId="qaCookies" />
                <label for="qaCookies">Accept optional cookies for QA/performance monitoring</label>
            </div>
        </div>
        <div class="flex items-center gap-1">
            <Button type="button" label="Register" @click="register_submit"></Button>
        </div>
    </div>
</template>

<script setup lang="ts">

import { useAuthStore } from '@/stores/auth'
import { ref } from 'vue'

const authStore = useAuthStore()
const showInvalidRegisterMessage = ref(false)
const showMissingFieldsMessage = ref(false)
const showPasswordMismatchMessage = ref(false)
const showRegisterError = ref(false)
const showGDPRMessage = ref(false)
const showRegistrationSuccess = ref(false)
const registrationErrorMessage = ref('')
const registrationSuccessMessage = ref('')

const showRegister = defineModel<boolean>('showRegister')
const closeDialogPlease = defineModel<boolean>('closeDialogPlease')

const username = ref('')
const password = ref('')
const passwordCheck = ref('')
const email = ref('')
const gdprAck = ref(false)
const qaCookies = ref(false)

const register_submit = async () => {
    // Reset all messages
    showInvalidRegisterMessage.value = false
    showMissingFieldsMessage.value = false
    showPasswordMismatchMessage.value = false
    showRegisterError.value = false
    showGDPRMessage.value = false
    showRegistrationSuccess.value = false
    registrationErrorMessage.value = ''
    registrationSuccessMessage.value = ''
    
    // Validation
    if (username.value === '' || password.value === '' || email.value === '') {
        showMissingFieldsMessage.value = true
        return;
    } 
    
    if (password.value !== passwordCheck.value) {
        showPasswordMismatchMessage.value = true
        return;
    }
    
    if (!gdprAck.value) {
        showGDPRMessage.value = true
        return;
    }

    try {
        const SIGNUP_URL = '/api/signup/'
        
        // Get CSRF token if needed
        const csrfToken = await authStore.getCsrfToken()
        const headers: HeadersInit = {
            'Content-Type': 'application/json'
        }
        
        if (csrfToken) {
            headers['X-CSRFToken'] = csrfToken
        }

        const response = await fetch(SIGNUP_URL, {
            method: 'POST',
            headers,
            credentials: 'include',
            body: JSON.stringify({
                username: username.value,
                password: password.value,
                email: email.value
            })
        })

        const data = await response.json()

        if (response.ok && data.success) {
            // Registration successful - show success message
            showRegistrationSuccess.value = true
            registrationSuccessMessage.value = data.message || 'Registration successful! Your account is pending admin approval.'
            
            // Clear form
            username.value = ''
            password.value = ''
            passwordCheck.value = ''
            email.value = ''
            
            // Close the registration form after a delay
            setTimeout(() => {
                showRegister.value = false
                closeDialogPlease.value = true
            }, 3000)
            
        } else {
            // Registration failed - show error
            showInvalidRegisterMessage.value = true
            registrationErrorMessage.value = data.error || 'Registration failed. Please try again.'
        }
        
    } catch (error) {
        console.error('Registration error:', error)
        showRegisterError.value = true
        registrationErrorMessage.value = 'Network error. Please check your connection and try again.'
    }
}

</script>

<style scoped>
.register-form {
    display: flex;
    flex-direction: column;
    gap: 0.4rem;
}

.title-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
}

.title {
    margin: 0;
}

.subtitle {
    margin: 0 0 0.5rem 0;
}

.form-section {
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
}

.form-section h2 {
    margin: 0.1rem 0 0;
    font-size: 0.95rem;
}

.consent-copy {
    display: flex;
    flex-direction: column;
    gap: 0.2rem;
    margin-top: 0.15rem;
    font-size: 0.92rem;
}

.consent-copy p {
    margin: 0;
}

.consent-row {
    display: flex;
    align-items: flex-start;
    gap: 0.45rem;
    font-size: 0.92rem;
    margin-top: 0.2rem;
}

.error {
    /* Use style built in to primevue */
    color: var(--p-button-text-danger-color);
}

.success {
    color: #000000;
}

.status-stack {
    display: flex;
    flex-direction: column;
    gap: 0.35rem;
}

.status-banner {
    margin: 0;
    padding: 0.55rem 0.75rem;
    border-radius: 0.375rem;
    border: 1px solid transparent;
    font-weight: 500;
}

.status-banner.error {
    background: #fee2e2;
    border-color: #fca5a5;
    color: #991b1b;
}

.status-banner.success {
    background: #cdeebb;
    border: 1px solid var(--p-button-success-border-color);
}

.wide {
    width: 100%;
}
</style>